// src/views/pages/restaurant-reservations/waitlist/components/AddWalkInModal.jsx
// (We keep filename so your imports don't change, but it becomes a Drawer UI)

import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Drawer, Typography, Space, Button, Divider, Form, Input, InputNumber, Select, Switch } from "antd";
import { UserPlus, Phone, Mail, Users, MapPin, FileText, Clock, X } from "react-feather";

const { Title, Text } = Typography;

const FieldCard = ({ icon, label, children }) => (
  <div
    style={{
      display: "flex",
      gap: 12,
      padding: 12,
      borderRadius: 14,
      border: "1px solid #eef2f7",
      background: "#fff",
      alignItems: "flex-start",
    }}
  >
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: 12,
        display: "grid",
        placeItems: "center",
        background: "#f3f4f6",
        flex: "0 0 auto",
      }}
    >
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 900, color: "#6b7280", letterSpacing: 0.2 }}>{label}</div>
      <div style={{ marginTop: 6 }}>{children}</div>
    </div>
  </div>
);

FieldCard.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.string,
  children: PropTypes.node,
};

export default function AddWalkInModal({
  open,
  onClose,
  locations = [],
  onSubmit,
}) {
  // ✅ Hooks must NEVER be conditional (fixes your ESLint hook warning)
  const [form] = Form.useForm();

  const locationOptions = useMemo(
    () => (Array.isArray(locations) ? locations : []).filter(Boolean).map((l) => ({ value: l, label: l })),
    [locations]
  );

  const handleFinish = async (values) => {
    // normalize
    const payload = {
      name: String(values.name || "").trim(),
      phone: String(values.phone || "").trim(),
      email: values.email ? String(values.email).trim() : null,
      partySize: Number(values.partySize || 2),
      location: values.location || null,
      notes: values.notes ? String(values.notes) : null,
      source: values.source || "Walk-in",
      estimatedWait: values.estimatedWait ? Number(values.estimatedWait) : null,
      paidSkip: !!values.paidSkip,
      isVIP: !!values.isVIP,
    };

    await onSubmit?.(payload);
    form.resetFields();
  };

  return (
    <Drawer
      placement="right"
      open={open}
      onClose={() => {
        form.resetFields();
        onClose?.();
      }}
      width={560}
      zIndex={99999}
      rootStyle={{ position: "fixed" }}
      mask={false}
      closable={false}
      bodyStyle={{ background: "#f6f8fc", padding: 18 }}
      headerStyle={{ background: "#fff", borderBottom: "1px solid #eef2f7", padding: "14px 18px" }}
      title={
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <Title level={4} style={{ margin: 0, fontWeight: 900, lineHeight: 1.2 }}>
              Add Walk-in
            </Title>
            <div style={{ marginTop: 6, color: "#6b7280", fontSize: 12 }}>
              Create a walk-in entry and place them into the waitlist.
            </div>
          </div>

          <Space>
            <Button
              icon={<X size={16} />}
              onClick={() => {
                form.resetFields();
                onClose?.();
              }}
              style={{ borderRadius: 12 }}
            >
              Close
            </Button>

            <Button
              type="primary"
              icon={<UserPlus size={16} />}
              onClick={() => form.submit()}
              style={{ borderRadius: 12 }}
            >
              Add
            </Button>
          </Space>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          partySize: 2,
          source: "Walk-in",
          paidSkip: false,
          isVIP: false,
          estimatedWait: null,
          location: undefined,
          notes: "",
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <FieldCard icon={<UserPlus size={16} />} label="Guest Name">
            <Form.Item
              name="name"
              rules={[{ required: true, message: "Guest name is required" }]}
              style={{ margin: 0 }}
            >
              <Input placeholder="e.g., Rajesh" size="large" />
            </Form.Item>
          </FieldCard>

          <FieldCard icon={<Phone size={16} />} label="Phone">
            <Form.Item
              name="phone"
              rules={[{ required: true, message: "Phone is required" }]}
              style={{ margin: 0 }}
            >
              <Input placeholder="e.g., 09779906203" size="large" />
            </Form.Item>
          </FieldCard>

          <FieldCard icon={<Mail size={16} />} label="Email (optional)">
            <Form.Item name="email" style={{ margin: 0 }}>
              <Input placeholder="e.g., demouser@netshine.com" size="large" />
            </Form.Item>
          </FieldCard>

          <FieldCard icon={<Users size={16} />} label="Party Size">
            <Form.Item
              name="partySize"
              rules={[{ required: true, message: "Party size is required" }]}
              style={{ margin: 0 }}
            >
              <InputNumber min={1} style={{ width: "100%" }} size="large" />
            </Form.Item>
          </FieldCard>

          <FieldCard icon={<MapPin size={16} />} label="Location Preference">
            <Form.Item name="location" style={{ margin: 0 }}>
              <Select
                allowClear
                placeholder="Select floor / area"
                options={locationOptions}
                size="large"
              />
            </Form.Item>
          </FieldCard>

          <FieldCard icon={<Clock size={16} />} label="Estimated Wait (minutes)">
            <Form.Item name="estimatedWait" style={{ margin: 0 }}>
              <InputNumber min={0} style={{ width: "100%" }} size="large" placeholder="Auto / leave blank" />
            </Form.Item>
          </FieldCard>
        </div>

        <Divider style={{ margin: "14px 0" }} />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <FieldCard icon={<FileText size={16} />} label="Notes (optional)">
            <Form.Item name="notes" style={{ margin: 0 }}>
              <Input.TextArea rows={4} placeholder="Allergy, birthday, seating request…" />
            </Form.Item>
          </FieldCard>

          <FieldCard icon={<MapPin size={16} />} label="Flags">
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <Form.Item name="isVIP" valuePropName="checked" style={{ margin: 0 }}>
                <Switch /> <Text style={{ marginLeft: 10 }}>VIP guest</Text>
              </Form.Item>

              <Form.Item name="paidSkip" valuePropName="checked" style={{ margin: 0 }}>
                <Switch /> <Text style={{ marginLeft: 10 }}>Paid skip</Text>
              </Form.Item>

              <Form.Item name="source" style={{ margin: 0 }}>
                <Select
                  size="large"
                  options={[
                    { value: "Walk-in", label: "Walk-in" },
                    { value: "Phone", label: "Phone" },
                    { value: "Website", label: "Website" },
                    { value: "Booking", label: "Booking" },
                  ]}
                />
              </Form.Item>
            </Space>
          </FieldCard>
        </div>

        {/* Mobile responsiveness */}
        <style>{`
          @media (max-width: 720px) {
            .ant-drawer-body > form > div[style*="grid-template-columns: 1fr 1fr"] {
              grid-template-columns: 1fr !important;
            }
          }
          .ant-btn { border-radius: 12px; }
          .ant-input, .ant-input-number, .ant-select-selector { border-radius: 12px !important; }
        `}</style>
      </Form>
    </Drawer>
  );
}

AddWalkInModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  locations: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
};
